import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Autoplay, Navigation, Pagination} from 'swiper/modules'
import Hero from '../hero'
import {scheduleAfterPaint} from '../../../../utils/common-utils'

export const HeroSlider = ({regions = [], enableAutoScoll}) => {
    const slideRegion = regions.find((r) => r.id === 'slide')
    const slides =
        slideRegion?.components?.map((comp) => ({
            ...comp?.data,
            regions: comp?.regions
        })) || []
    const containerRef = useRef(null)
    const [renderAllSlides, setRenderAllSlides] = useState(false)
    const autoScrollProps = enableAutoScoll
        ? {
              autoplay: {
                  delay: 5000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true
              },
              onTouchStart: (swiper) => swiper.autoplay?.stop?.(),
              onTouchEnd: (swiper) => {
                  setTimeout(() => swiper.autoplay?.start?.(), 1500)
              },
              onSlideChange: (swiper) => {
                  setTimeout(() => swiper.autoplay?.start?.(), 1500)
              }
          }
        : {}
    useEffect(() => {
        let cancelled = false
        let task
        const media = containerRef.current?.querySelector('.swiper-slide img, .swiper-slide video')
        const prepareSlides = () => {
            scheduleAfterPaint(() => {
                if (cancelled) return
                task = setTimeout(() => {
                    if (!cancelled) setRenderAllSlides(true)
                }, 0)
            })
        }
        if (
            !media ||
            media.complete ||
            media.readyState >= 2 ||
            (media.tagName === 'VIDEO' && !media.autoplay)
        )
            prepareSlides()
        else {
            media.addEventListener('load', prepareSlides, {once: true})
            media.addEventListener('loadeddata', prepareSlides, {once: true})
            media.addEventListener('error', prepareSlides, {once: true})
        }
        return () => {
            cancelled = true
            clearTimeout(task)
            media?.removeEventListener('load', prepareSlides)
            media?.removeEventListener('loadeddata', prepareSlides)
            media?.removeEventListener('error', prepareSlides)
        }
    }, [slideRegion])
    if (!slides.length) return null

    return (
        <Box
            ref={containerRef}
            onPointerEnter={() => setRenderAllSlides(true)}
            onPointerDownCapture={() => setRenderAllSlides(true)}
            onFocusCapture={() => setRenderAllSlides(true)}
            w="100%"
            sx={{
                '.swiper-pagination': {
                    bottom: '10px !important',
                    left: '50% !important',
                    transform: 'translateX(-50%) !important',
                    width: 'fit-content !important'
                },
                '.hero-carousel .swiper-button-next, .hero-carousel .swiper-button-prev': {
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                },
                '.hero-carousel:hover .swiper-button-next, .hero-carousel:hover .swiper-button-prev':
                    {
                        opacity: 1
                    },
                '.hero-carousel .swiper-button-next': {
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)'
                },
                '.hero-carousel .swiper-button-prev': {
                    left: '20px',
                    top: '50%',
                    transform: 'translateY(-50%) rotate(180deg)'
                },
                '.hero-carousel .swiper-pagination-bullet': {
                    width: '6px',
                    height: '6px',
                    background: 'transparent',
                    border: '1px solid white',
                    borderRadius: '50%',
                    opacity: '1'
                },
                '.hero-carousel .swiper-pagination-bullet-active': {
                    background: 'white',
                    borderColor: 'white'
                },
                '@media screen and (min-width: 1200px)': {
                    '.hero-carousel .swiper-pagination': {
                        display: 'none'
                    }
                },
                '@media screen and (max-width: 1200px)': {
                    '.hero-carousel .swiper-button-next': {
                        display: 'none'
                    },
                    '.hero-carousel .swiper-button-prev': {
                        display: 'none'
                    }
                },
                '.hero-carousel .swiper-slide': {
                    opacity: '0',
                    visibility: 'hidden'
                },
                // SSR has no Swiper active classes. Paint the first hero while
                // hydration initializes the carousel, then let Swiper take over.
                '.hero-carousel:not(.swiper-initialized) > .swiper-wrapper > .swiper-slide:first-child':
                    {
                        opacity: '1',
                        visibility: 'visible'
                    },
                '.hero-carousel .swiper-slide.swiper-slide-active, .hero-carousel .swiper-slide.swiper-slide-next, .hero-carousel.swiper-slide.swiper-slide-prev':
                    {
                        opacity: '1',
                        visibility: 'visible'
                    }
            }}
        >
            <Swiper
                className="hero-carousel"
                modules={[Autoplay, Navigation, Pagination]}
                loop
                pagination={{clickable: true}}
                navigation
                {...autoScrollProps}
            >
                {(renderAllSlides ? slides : slides?.slice(0, 1))?.map((slideProps, idx) => (
                    <SwiperSlide key={idx}>
                        <Hero
                            {...slideProps}
                            eagerLoadImage={idx === 0 && slideProps.eagerLoadImage}
                            preloadVideoPoster={idx === 0}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
        </Box>
    )
}

HeroSlider.propTypes = {
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object.isRequired
                })
            )
        })
    ),
    enableAutoScoll: PropTypes.bool
}

export default HeroSlider
